"use client";

import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrimaryActionButton } from "@/components/ui/PrimaryActionButton";
import { ThreadsAccountCard } from "./ThreadsAccountCard";
import { SiThreads } from "react-icons/si";
import { GlassPanel } from "@/components/ui/GlassPanel";
import {
  ThreadsAccount,
  useThreadsAccounts,
  UseThreadsAccountsReturn,
} from "../hooks/useThreadsAccounts";

type ThreadsSectionVariant = "page" | "popup";

type ThreadsSectionProps = {
  hook?: UseThreadsAccountsReturn;
  variant?: ThreadsSectionVariant;
  onBack?: () => void;
  onConnectMore?: () => void;
  redirectTo?: string;
};

export function ThreadsSection({
  hook: providedHook,
  variant = "page",
  onBack,
  onConnectMore,
  redirectTo,
}: ThreadsSectionProps = {}) {
  const { t } = useTranslation();
  const hook = providedHook ?? useThreadsAccounts();
  const { isLoading, isConnecting, accounts, connectThreads, disconnectThreads } = hook;

  const hasAccounts = Array.isArray(accounts) && accounts.length > 0;

  const sectionClassName = variant === "popup" ? "text-white space-y-6 w-full" : "rounded-lg shadow-sm text-white";
  const connectMoreTranslation = t("account.connect_more_accounts");
  const connectMoreText =
    connectMoreTranslation && connectMoreTranslation !== "account.connect_more_accounts"
      ? connectMoreTranslation
      : "Connect more accounts";

  const handleConnect = useCallback(() => {
    const fallbackRedirect = typeof window !== "undefined" ? window.location.pathname : "/account";
    return connectThreads(redirectTo ?? fallbackRedirect);
  }, [connectThreads, redirectTo]);

  if (isLoading) {
    return (
      <div className={sectionClassName}>
        <div className="flex items-center justify-between mb-4">
          {variant === "popup" ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white"
                onClick={onBack}
                disabled={!onBack}
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Back</span>
              </Button>
              <h3 className="text-2xl font-normal">Threads</h3>
              <div className="h-8 w-8" />
            </>
          ) : (
            <h3 className="text-2xl font-normal mb-4">Threads</h3>
          )}
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-neutral-800 rounded w-3/4 mb-4"></div>
          <div className="h-8 bg-neutral-800 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  const header = (
    <div className="flex items-center justify-between mb-4">
      {variant === "popup" ? (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white"
            onClick={onBack}
            disabled={!onBack}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back</span>
          </Button>
          <h3 className="text-2xl font-normal">Threads</h3>
          <div className="h-8 w-8" />
        </>
      ) : (
        <h3 className="text-2xl font-normal mb-4">Threads</h3>
      )}
    </div>
  );

  const connectButton = (
    <PrimaryActionButton size="sm" className="w-full" onClick={handleConnect} disabled={isConnecting}>
      <SiThreads className="h-4 w-4 text-black" />
      {isConnecting ? t("account.connecting") || "Connecting..." : t("account.connect_with_threads") || "Connect with Threads"}
    </PrimaryActionButton>
  );

  const content = hasAccounts ? (
    <div className="space-y-4">
      {(accounts || []).map((account: ThreadsAccount) => (
        <ThreadsAccountCard key={account.id} account={account} onDisconnect={disconnectThreads} />
      ))}
    </div>
  ) : (
    <GlassPanel className="text-center p-4 md:p-6 space-y-6 ">
      <SiThreads className="mx-auto h-12 w-12" />
      <h3 className="text-lg font-medium">{t("account.no_threads_accounts_found") || "No Threads Accounts Found"}</h3>

      <div className="space-y-3 max-w-sm mx-auto mt-6">{connectButton}</div>
    </GlassPanel>
  );

  const connectMoreButton =
    variant === "popup" && hasAccounts && onConnectMore ? (
      <Button
        type="button"
        variant="ghost"
        className="w-full border border-white/10 bg-white/5 text-white hover:bg-white/10"
        onClick={onConnectMore}
      >
        {connectMoreText}
      </Button>
    ) : null;

  return (
    <div className={sectionClassName}>
      {header}
      {content}
      {connectMoreButton}
    </div>
  );
}
